import { NgIf, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ExtendedNotificationComponent } from '@next/core-lib/components';
import { AgrNumberPipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, take } from 'rxjs/operators';
import { MIN_FLOCKS_FOR_BENCHMARK } from 'src/app/shared/constants';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';

import { DataViewTableDetailedPrintComponent } from './data-view-table-detailed-print/data-view-table-detailed-print.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import {
  TableColumn,
  TableColumnStickyTypes,
  TableColumnTypes,
  TableConfig,
} from '../../../../shared/components/table/table.model';
import {
  BenchmarkData,
  BenchmarkSettings,
  DataViewType,
  FarmerInsightsModuleState,
  FlockDataParameter,
  KpiGroup,
  KpiGroupingChild,
  NormalizedTimeSeries,
} from '../../../models/farmer-insights.model';
import {
  getBenchmarkCustomerAveragSelectedPeriodData,
  getBenchmarkLastWeeksData,
  getBenchmarkSelectedPeriodData,
  getBenchmarkSettings,
  getKpiGroups,
  getNormalizedTimeSeriesArray,
  getSelectedDataViewType,
  getSelectedKpis,
  getTimeSeries,
} from '../../../store/selectors/farmer-insights.selectors';

@Component({
  selector: 'app-data-view-table-detailed',
  templateUrl: './data-view-table-detailed.component.html',
  styleUrls: ['./data-view-table-detailed.component.scss'],
  imports: [
    NgIf,
    ExtendedNotificationComponent,
    TableComponent,
    DataViewTableDetailedPrintComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DataViewTableDetailedComponent {
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);
  private decimalPipe = inject(AgrNumberPipe);

  canPrintMaxParameters = 17;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    customFooter: [false, false, false],
    scrolling: true,
    wrapperWidth: '100%',
  };

  private readonly locationColumns: TableColumn[] = [
    {
      id: 'locationName',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('farmer-insights.location'),
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'breeds',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('breed'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
  ];

  private readonly cycleColumns: TableColumn[] = [
    {
      id: 'locationName',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('farmer-insights.location'),
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'cycleNumber',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('cycle'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'breeds',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('breed'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'startDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('start-date'),
      customfooterValues: ['', '', ''],
    },
    {
      id: 'endDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('cycle-overview.panel.departuredate'),
      customfooterValues: ['', '', ''],
    },
  ];

  private readonly cycleBarnColumns: TableColumn[] = [
    {
      id: 'locationName',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('farmer-insights.location'),
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'cycleNumber',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('cycle'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'barnName',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('departure.barn'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'breeds',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('breed'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'startDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('start-date'),
      customfooterValues: ['', '', ''],
    },
    {
      id: 'endDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('cycle-overview.panel.departuredate'),
      customfooterValues: ['', '', ''],
    },
  ];

  private readonly locationBarnColumns: TableColumn[] = [
    {
      id: 'locationName',
      type: TableColumnTypes.STRING,

      label: this.locale.translate('farmer-insights.location'),
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'barnName',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('departure.barn'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },
    {
      id: 'breeds',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('breed'),
      customfooterValues: ['', '', ''],
      sticky: TableColumnStickyTypes.START,
    },

    {
      id: 'startDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('start-date'),
      customfooterValues: ['', '', ''],
    },
    {
      id: 'endDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('cycle-overview.panel.departuredate'),
      customfooterValues: ['', '', ''],
    },
  ];

  public normalizedTimeSeriesArray$: Observable<NormalizedTimeSeries[]> = this.store
    .select(getNormalizedTimeSeriesArray)
    .pipe(distinctUntilChanged());
  public selectedDataViewType$: Observable<DataViewType> = this.store
    .select(getSelectedDataViewType)
    .pipe(distinctUntilChanged());

  public readonly selectedKpis$: Observable<FlockDataParameter[]> = this.store
    .select(getSelectedKpis)
    .pipe(distinctUntilChanged());
  private readonly kpiGroups$: Observable<KpiGroup[]> = this.store
    .select(getKpiGroups)
    .pipe(distinctUntilChanged());

  public timeseries$: Observable<DataSeries<number>> = this.store.select(getTimeSeries);
  public getBenchmarkSelectedPeriodData$: Observable<BenchmarkData> = this.store
    .select(getBenchmarkSelectedPeriodData)
    .pipe(distinctUntilChanged());
  public benchmarkLastWeeksData$: Observable<BenchmarkData> = this.store
    .select(getBenchmarkLastWeeksData)
    .pipe(distinctUntilChanged());
  public benchmarkCustomerAverageSelectedPeriodData$: Observable<BenchmarkData> = this.store

    .select(getBenchmarkCustomerAveragSelectedPeriodData)
    .pipe(distinctUntilChanged());
  public benchmarkSettings$: Observable<BenchmarkSettings> = this.store
    .select(getBenchmarkSettings)
    .pipe(distinctUntilChanged());
  public benchmarkSettings: BenchmarkSettings = null;

  public parameters: DataSeriesParameter[] = [];

  public tableConfig$: Observable<TableConfig> = combineLatest([
    this.normalizedTimeSeriesArray$,
    this.selectedDataViewType$,
    this.selectedKpis$,
    this.kpiGroups$,
    this.timeseries$,
    this.getBenchmarkSelectedPeriodData$,
    this.benchmarkLastWeeksData$,
    this.benchmarkCustomerAverageSelectedPeriodData$,
    this.benchmarkSettings$,
  ]).pipe(
    distinctUntilChanged(),
    skipWhile(
      ([
        _normalizedTimeSeriesArray,
        dataViewType,
        kpis,
        _kpiGroups,
        _timeSeries,
        benchmarkSelectedPeriodData,
        benchmarkLastWeeksData,
        benchmarkCustomerAverageSelectedPeriodData,
        benchmarkSettings,
      ]: [
        NormalizedTimeSeries[],
        DataViewType,
        FlockDataParameter[],
        KpiGroup[],
        DataSeries<number, Date>,
        BenchmarkData,
        BenchmarkData,
        BenchmarkData,
        BenchmarkSettings,
      ]) =>
        dataViewType === null ||
        kpis === null ||
        _timeSeries === null ||
        benchmarkSelectedPeriodData === null ||
        benchmarkLastWeeksData === null ||
        benchmarkCustomerAverageSelectedPeriodData === null ||
        benchmarkSettings === null,
    ),
    map(
      ([
        normalizedTimeSeriesArray,
        dataViewType,
        kpis,
        kpiGroups,

        timeseries,
        benchmarkSelectedPeriodData,
        benchmarkLastWeeksData,
        benchmarkCustomerAverageSelectedPeriodData,
        benchmarkSettings,
      ]: [
        NormalizedTimeSeries[],
        DataViewType,
        FlockDataParameter[],
        KpiGroup[],
        DataSeries<number, Date>,
        BenchmarkData,
        BenchmarkData,
        BenchmarkData,
        BenchmarkSettings,
      ]) => {
        this.parameters = [];

        const allKpis: KpiGroupingChild[] = (<KpiGroup[]>kpiGroups)
          .map((d) => d.children)
          .reduce((acc, val) => acc.concat(val), [])
          .sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
        const columnIds: string[] = [];
        let columns: TableColumn[] = [];
        let baseWidth: number;

        switch (dataViewType) {
          case DataViewType.PerLocation:
            baseWidth = 600;
            columns = columns.concat(this.locationColumns);
            break;
          case DataViewType.PerCycle:
            baseWidth = 800;
            columns = columns.concat(this.cycleColumns);
            break;
          case DataViewType.PerCycleBarn:
            baseWidth = 1000;
            columns = columns.concat(this.cycleBarnColumns);
            break;
          case DataViewType.PerLocationBarn:
            baseWidth = 1000;
            columns = columns.concat(this.locationBarnColumns);
            break;
        }

        if (timeseries) {
          // update 1st column: add titles in customFooterValues
          const customfooterValues = [this.locale.translate('farmer-insights.aggregation.Sum')];

          if (benchmarkSettings.selectedPeriod) {
            if (benchmarkSelectedPeriodData?.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK) {
              customfooterValues.push(
                `${this.tenantTranslation.translate('farmer-insights.custom-footers.same-period')} ${
                  benchmarkSelectedPeriodData?.flocksUsed ?? 0
                })`,
              );
            } else {
              customfooterValues.push(
                `${this.tenantTranslation.translate('farmer-insights.custom-footers.same-period')} ${
                  benchmarkSelectedPeriodData?.flocksUsed ?? 0
                }): ${this.locale.translate('farmer-insights.no-benchmark')}`,
              );
            }
          }
          if (benchmarkSettings.lastWeeks) {
            if (benchmarkLastWeeksData?.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK) {
              customfooterValues.push(
                `${this.tenantTranslation.translate('farmer-insights.custom-footers.last-weeks')} ${
                  benchmarkLastWeeksData?.flocksUsed ?? 0
                })`,
              );
            } else {
              customfooterValues.push(
                `${this.tenantTranslation.translate('farmer-insights.custom-footers.last-weeks')} ${
                  benchmarkLastWeeksData?.flocksUsed ?? 0
                }): ${this.locale.translate('farmer-insights.no-benchmark')}`,
              );
            }
          }

          if (benchmarkSettings.customerAverageSelectedPeriod) {
            customfooterValues.push(
              `${this.locale.translate(
                'farmer-insights.custom-footers.customer-average-selected-period',
              )} ${benchmarkCustomerAverageSelectedPeriodData?.flocksUsed ?? 0})`,
            );
          }

          const locationNameColumn = columns.find((col) => col.id === 'locationName');
          if (locationNameColumn) {
            locationNameColumn.customfooterValues = customfooterValues;
          }

          // add a column for each KPI and set the customFooters for that KPI if applicable
          allKpis.forEach((kpiGroupingChild: KpiGroupingChild) => {
            const metaDataParam = timeseries.metaData.parameters.find(
              (d) => d.code === kpiGroupingChild.kpi.toString(),
            );
            if (metaDataParam !== undefined) {
              const decimalPrecision = metaDataParam.numberOfDecimals;
              let label = `${this.locale.translate(
                `farmer-insights.kpis.${kpiGroupingChild.kpi}`,
              )}`;
              if (metaDataParam.unit !== undefined && metaDataParam.unit.length > 0) {
                label += ` (${this.locale.translate(metaDataParam.unit)})`;
              }
              columns.push({
                id: kpiGroupingChild.kpi.toString(),
                type: TableColumnTypes.NUMBER,
                label,
                digitsInfo: `1.${decimalPrecision}-${decimalPrecision}`,
                customfooterValues: this.getCustomFooterValues(
                  kpiGroupingChild,
                  timeseries,
                  benchmarkSelectedPeriodData,
                  benchmarkLastWeeksData,
                  benchmarkCustomerAverageSelectedPeriodData,
                  benchmarkSettings,
                ),
              });
              if (kpis.indexOf(kpiGroupingChild.kpi) === -1) {
                columnIds.push(kpiGroupingChild.kpi);
              }
            }
          });
        }

        return <TableConfig>{
          ...this.tableConfigBase,
          customFooter: this.getCustomFooterVisibility(columns),
          columns,
          hiddenColumns: columnIds,
          width: `${baseWidth + kpis.length * 100}px`,
          dataSource: new MatTableDataSource(
            normalizedTimeSeriesArray.map((ts) => {
              const dataObject: { [k: string]: unknown } = {
                locationName: ts.locationName,
                cycleNumber: ts.cycleNumber,
                barnName: ts.barnName,
                startDate: ts.fromDate,
                endDate: ts.toDate,
                breeds: ts.breeds,
              };

              ts.kpiValues.forEach((kpi) => {
                if (kpi.value === undefined || isNaN(kpi.value) || !isFinite(kpi.value)) {
                  this.parameters.push(kpi.parameter);
                  dataObject[kpi.parameter.code] = undefined;
                } else {
                  this.parameters.push(kpi.parameter);
                  dataObject[kpi.parameter.code] = kpi.value;
                }
              });
              return dataObject;
            }),
          ),
        };
      },
    ),
  );

  private getCustomFooterValues(
    kpiGroupingChild: KpiGroupingChild,
    timeseries: DataSeries<number>,
    benchmarkSelectedPeriodData: BenchmarkData,
    benchmarkLastWeeksData: BenchmarkData,
    benchmarkCustomerAverageSelectedPeriodData: BenchmarkData,
    benchmarkSettings: BenchmarkSettings,
  ): string[] {
    const result: string[] = [];
    // cumulative
    if (!!timeseries && !!timeseries.series[kpiGroupingChild.kpi]) {
      let benchmarkValue = '';
      // Check whether this KPI has a benchmark and add it if that's the case. Otherwise hide it.
      const foundBenchmark = timeseries.series[kpiGroupingChild.kpi].find(
        (val) => val.name === 'Total',
      );
      const metaDataParameter = timeseries.metaData.parameters.find(
        (p) => p.code === kpiGroupingChild.kpi,
      );
      if (!!foundBenchmark && isFinite(foundBenchmark.values[0].value) && !!metaDataParameter) {
        benchmarkValue = String(
          this.decimalPipe.transform(
            foundBenchmark.values[0].value,
            `1.${metaDataParameter.numberOfDecimals}-${metaDataParameter.numberOfDecimals}`,
          ),
        );
      }
      result.push(benchmarkValue);
    }

    // benchmark selected period
    if (benchmarkSettings.selectedPeriod) {
      if (!!benchmarkSelectedPeriodData && benchmarkSelectedPeriodData.flocksUsed >= 0) {
        let benchmarkValue = '';
        // Check whether this KPI has a benchmark and add it if that's the case. Otherwise hide it.
        const foundBenchmark = benchmarkSelectedPeriodData.benchmarks.find(
          (benchmark) => benchmark.parameter.code === kpiGroupingChild.kpi.toString(),
        );
        if (
          !!foundBenchmark &&
          isFinite(foundBenchmark.value) &&
          benchmarkSelectedPeriodData.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK
        ) {
          benchmarkValue = this.decimalPipe.transform(
            foundBenchmark.value,
            `1.${foundBenchmark.parameter.numberOfDecimals}-${foundBenchmark.parameter.numberOfDecimals}`,
          );
        }
        result.push(benchmarkValue);
      }
    }

    // benchmark last 10 weeks
    if (benchmarkSettings.lastWeeks) {
      if (!!benchmarkLastWeeksData && benchmarkLastWeeksData.flocksUsed >= 0) {
        let benchmarkValue = '';
        // Check whether this KPI has a benchmark and add it if that's the case. Otherwise hide it.
        const foundBenchmark = benchmarkLastWeeksData.benchmarks.find(
          (benchmark) => benchmark.parameter.code === kpiGroupingChild.kpi.toString(),
        );
        if (
          !!foundBenchmark &&
          isFinite(foundBenchmark.value) &&
          benchmarkLastWeeksData.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK
        ) {
          benchmarkValue = this.decimalPipe.transform(
            foundBenchmark.value,
            `1.${foundBenchmark.parameter.numberOfDecimals}-${foundBenchmark.parameter.numberOfDecimals}`,
          );
        }
        result.push(benchmarkValue);
      }
    }

    // benchmark last 10 weeks
    if (benchmarkSettings.customerAverageSelectedPeriod) {
      if (benchmarkCustomerAverageSelectedPeriodData) {
        let benchmarkValue = '';
        // Check whether this KPI has a benchmark and add it if that's the case. Otherwise hide it.
        const foundBenchmark = benchmarkCustomerAverageSelectedPeriodData.benchmarks.find(
          (benchmark) => benchmark.parameter.code === kpiGroupingChild.kpi.toString(),
        );
        if (!!foundBenchmark && isFinite(foundBenchmark.value)) {
          benchmarkValue = this.decimalPipe.transform(
            foundBenchmark.value,
            `1.${foundBenchmark.parameter.numberOfDecimals}-${foundBenchmark.parameter.numberOfDecimals}`,
          );
        }
        result.push(benchmarkValue);
      }
    }

    return result;
  }

  private getCustomFooterVisibility(columns: TableColumn[]): boolean[] {
    return [0, 1, 2, 3].map((footerIndex) =>
      columns.some((column) => {
        const value = column.customfooterValues?.[footerIndex];
        return value !== undefined && value !== null && value !== '';
      }),
    );
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.benchmarkSettings$.pipe(take(1)).subscribe((settings) => {
      this.benchmarkSettings = settings;
    });
  }
}
